import { RichTranslator, useTranslator } from '@bluebeam/react-i18n';
import BreadcrumbsOfAppState from 'components/BreadcrumbsOfAppState';
import DrawingsBrowser from 'components/DrawingsBrowser';
import FileBrowserBar from 'components/FileBrowser/FileBrowserBar';
import ProjectFileBrowser from 'components/FileBrowser/ProjectFileBrowser';
import BasicBlueprint from 'components/Icons/BasicBlueprint';
import Studio from 'components/Icons/Studio';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect';
import Label from 'components/inputs/Label';
import LoadingAnimation from 'components/LoadingAnimation';
import {
  getAccountName,
  getAccountShortName,
  getIntegrationOutlineIcon,
} from 'constants/integrations';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import { useIntegrationsCheck } from 'hooks/useIntegrationsCheck';
import { List, OrderedMap } from 'immutable';
import StudioProjectsList from 'pages/DrawingsPage/StudioTab/StudioProjectsList';
import React, { useEffect, useState } from 'react';
import { ProjectDrive } from 'records/ProjectDrive';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { ConnectedAccount } from 'records/ConnectedAccount';
import ConnectedAccountFileBrowser from 'components/FileBrowser/ConnectedAccountFileBrowser';
import { Link } from 'react-router-dom';
import ModalStdHeader from '../ModalStdHeader';
import { InsertFilesOnSubmit } from './InsertFilesModal';

interface Props extends WithAppStoreProps {
  onSubmit: InsertFilesOnSubmit;
  drawingsDisabled?: boolean;
}

type SelectionSource =
  | {
      type: 'drawings';
    }
  | {
      type: 'studio';
    }
  | {
      type: 'bluebeamDms';
      providerType: string;
      drives: List<ProjectDrive>;
      drive: ProjectDrive;
    }
  | {
      type: 'connectedAccount';
      providerType: string;
      drives: List<ConnectedAccount>;
      drive: ConnectedAccount;
    }
  | {
      type: 'none';
    };

const InsertFilesModalFC: React.FC<Props> = ({
  actions,
  appStore,
  onSubmit,
  drawingsDisabled,
}: Props) => {
  const { state } = appStore;
  const {
    projectDriveSelectedItems,
    integrationsSelectedItems,
    selectedPages,
    studioProjects,
    studioSelectedFiles,
    selectedProject,
    studioSelectedFolders,
    studioCurrentProjectId,
    studioCurrentFolderId,
  } = state;

  const [projectDrives, setProjectDrives] = useState<
    List<ProjectDrive> | undefined
  >();
  const [connectedAccounts, setConnectedAccounts] = useState<
    List<ConnectedAccount> | undefined
  >();
  const [currentSelectionSource, setCurrentSelectionSource] =
    useState<SelectionSource>({ type: drawingsDisabled ? 'none' : 'drawings' });
  const [searchInput, setSearchInput] = useState('');

  const clearSearchFilter = () => setSearchInput('');

  const onlyImagesSelectable: boolean = drawingsDisabled as boolean;
  const { translate } = useTranslator([
    'integrationsPage',
    'insertFilesModal',
    'buttons',
    'formLabels',
  ]);

  const { [FeatureFlag.ImportFromStudio]: isImportFromStudioEnabled } =
    useFeatureFlags();
  const { areAccountIntegrationsEnabled, areIntegrationsEnabled } =
    useIntegrationsCheck();

  useEffect(() => {
    (async () => {
      actions.getStudioProjects();

      if (areAccountIntegrationsEnabled) {
        const connectedAccountsAction = await actions.getConnectedAccounts();
        const filteredConnectedAccounts =
          connectedAccountsAction.connectedAccounts.filter(
            (connectedAccount) => !connectedAccount.isExpired
          );

        setConnectedAccounts(filteredConnectedAccounts);
        setProjectDrives(List());
      } else {
        const newAppState = await actions.getProjectDrives();
        const projectDrivesResult = newAppState.projectDrives;

        setProjectDrives(projectDrivesResult);
        setConnectedAccounts(List());
      }
    })();
  }, []);

  const selectedCount =
    projectDriveSelectedItems.count() +
    integrationsSelectedItems.count() +
    selectedPages.count() +
    studioSelectedFiles.length;

  const handleCancelClick = async (): Promise<void> => {
    const {
      clearAllModals,
      resetBreadcrumbs,
      resetProjectDriveSelection,
      resetIntegrationsSelectedItems,
      resetSelectedPages,
      clearStudioNavigation,
      setStudioSelectedFiles,
    } = actions;
    await resetBreadcrumbs();
    await resetProjectDriveSelection();
    await resetIntegrationsSelectedItems();
    await resetSelectedPages();
    await clearStudioNavigation();
    await setStudioSelectedFiles([]);
    clearAllModals();
  };

  const handleSubmit = async (): Promise<void> => {
    const studioData = {
      selectedProject,
      studioSelectedFolders,
      studioCurrentProjectId,
      studioFolderParentId:
        studioCurrentFolderId !== '' ? studioCurrentFolderId : '0',
    };

    onSubmit({
      selectedPages,
      projectDriveSelectedItems,
      integrationsSelectedItems,
      studioItems: List(studioSelectedFiles),
      studioProjectData: studioData,
    });

    handleCancelClick();
  };

  const handleSearchChange = (searchText: string) => {
    setSearchInput(searchText);
  };

  const drivesByProviderType = (projectDrives || List())
    .reduce((map, drive) => {
      const { providerType } = drive;
      const previousGroup = map.get(providerType, List());
      return map.set(providerType, previousGroup.push(drive));
    }, OrderedMap<string, List<ProjectDrive>>())
    .sortBy((_, providerType) =>
      getAccountShortName(providerType) === 'Account'
        ? translate('integrationsPage:accountDefaultShortName')
        : getAccountShortName(providerType)
    );

  const integrationByProviderType = (connectedAccounts || List())
    .reduce((map, drive) => {
      const { providerType } = drive;
      const previousGroup = map.get(providerType, List());
      return map.set(providerType, previousGroup.push(drive));
    }, OrderedMap<string, List<ConnectedAccount>>())
    .sortBy((_, providerType) =>
      getAccountShortName(providerType) === 'Account'
        ? translate('integrationsPage:accountDefaultShortName')
        : getAccountShortName(providerType)
    );

  useEffect(() => {
    if (currentSelectionSource.type !== 'none') return;
    if (projectDrives && projectDrives.count() > 0) {
      const [providerType, drives] = drivesByProviderType.toArray()[0];
      setCurrentSelectionSource({
        type: 'bluebeamDms',
        providerType,
        drives,
        drive: drives.first(),
      });
    }
  }, [projectDrives]);

  useEffect(() => {
    if (currentSelectionSource.type !== 'none') return;
    if (connectedAccounts && connectedAccounts.count() > 0) {
      const [providerType, drives] = integrationByProviderType.toArray()[0];
      setCurrentSelectionSource({
        type: 'connectedAccount',
        providerType,
        drives,
        drive: drives.first(),
      });
    }
  }, [connectedAccounts]);

  return (
    <div id="insert-files-modal" className="modal__content oh">
      <ModalStdHeader
        text={translate('insertFilesModal:modalTitle')}
        onClose={handleCancelClick}
      />
      <div className="oa row">
        <div className="column pt6 " style={{ width: '247px' }}>
          {!drawingsDisabled && (
            <div className="bb-3 pt6 pl5 pb5">
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: 14,
                  fontWeight: 600,
                  outline: 'none',
                  textAlign: 'left',
                  padding: '0',
                }}
                className={`row font-size-4 medium cursor-pointer mb5 ${
                  currentSelectionSource.type === 'drawings'
                    ? 'color-primary'
                    : 'black-2'
                }`}
                onClick={() => {
                  if (currentSelectionSource.type !== 'drawings')
                    setCurrentSelectionSource({ type: 'drawings' });
                }}
              >
                <div className="pr3">
                  <BasicBlueprint width="18px" height="15px" />
                </div>
                {translate('insertFilesModal:navigation.drawings')}
              </button>
              {isImportFromStudioEnabled && studioProjects.size !== 0 && (
                <button
                  id="from-studio-projects-btn"
                  className={`row font-size-4 medium cursor-pointer ${
                    currentSelectionSource.type === 'studio'
                      ? 'color-primary'
                      : 'black-2'
                  }`}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: 14,
                    fontWeight: 600,
                    outline: 'none',
                    textAlign: 'left',
                    padding: '0',
                  }}
                  onClick={() => {
                    if (currentSelectionSource.type !== 'studio')
                      setCurrentSelectionSource({ type: 'studio' });
                  }}
                >
                  <div className="pr3">
                    <Studio width="18px" height="15px" />
                  </div>
                  {translate('insertFilesModal:navigation.studio')}
                </button>
              )}
            </div>
          )}
          {areAccountIntegrationsEnabled ? (
            <div className="pl5 pt4">
              <div className="font-size-5 mt2 mb5 black-2 medium">
                {translate('insertFilesModal:navigation.integrations')}
              </div>
              {!connectedAccounts && (
                <LoadingAnimation
                  align="start"
                  removePadding
                  pageName="Loading Accounts"
                />
              )}
              {connectedAccounts && connectedAccounts.count() === 0 && (
                <>
                  <div className="font-size-4 mt2 black-2 medium">
                    {translate(
                      'insertFilesModal:integrations.emptyState.title'
                    )}
                  </div>
                  <div className="font-size-4 mt2 black-2">
                    <RichTranslator
                      components={[
                        <Link to="/connected-accounts" target="_blank" />,
                      ]}
                      translationKey="drawingsIntegrationsText:modal.accountIntegrations.empty.description"
                    />
                  </div>
                </>
              )}
              {connectedAccounts &&
                connectedAccounts.count() > 0 &&
                integrationByProviderType
                  .toArray()
                  .map(([providerType, drives]) => {
                    const isSelected =
                      currentSelectionSource.type === 'bluebeamDms' &&
                      currentSelectionSource.providerType === providerType;

                    const Icon = getIntegrationOutlineIcon(providerType);

                    return (
                      <div
                        key={providerType}
                        className={`font-size-4 medium cursor-pointer mb4 ${
                          isSelected ? 'color-primary' : 'black-2'
                        }`}
                        role="tab"
                        tabIndex={0}
                        onClick={() => {
                          if (!isSelected) {
                            setCurrentSelectionSource({
                              type: 'connectedAccount',
                              providerType,
                              drives,
                              drive: drives.first(),
                            });
                          }
                        }}
                      >
                        <Icon
                          className={`mr2 wrem-1p5 ${
                            isSelected ? 'fill-primary' : 'fill-gray-8'
                          }`}
                        />
                        {getAccountShortName(providerType) === 'Account'
                          ? translate(
                              'integrationsPage:accountDefaultShortName'
                            )
                          : getAccountShortName(providerType)}
                      </div>
                    );
                  })}
            </div>
          ) : (
            areIntegrationsEnabled && (
              <div className="pl5 pt4">
                <div className="font-size-5 mt2 mb5 black-2 medium">
                  {translate('insertFilesModal:navigation.integrations')}
                </div>
                {!projectDrives && (
                  <LoadingAnimation
                    align="start"
                    removePadding
                    pageName="Loading Accounts"
                  />
                )}
                {projectDrives && projectDrives.count() === 0 && (
                  <>
                    <div className="font-size-4 mt2 black-2 medium">
                      {translate(
                        'insertFilesModal:integrations.emptyState.title'
                      )}
                    </div>
                    <div className="font-size-4 mt2 black-2">
                      {translate(
                        'insertFilesModal:integrations.emptyState.description'
                      )}
                    </div>
                  </>
                )}
                {projectDrives &&
                  projectDrives.count() > 0 &&
                  drivesByProviderType
                    .toArray()
                    .map(([providerType, drives]) => {
                      const isSelected =
                        currentSelectionSource.type === 'bluebeamDms' &&
                        currentSelectionSource.providerType === providerType;

                      const Icon = getIntegrationOutlineIcon(providerType);

                      return (
                        <div
                          key={providerType}
                          className={`font-size-4 medium cursor-pointer mb4 ${
                            isSelected ? 'color-primary' : 'black-2'
                          }`}
                          role="tab"
                          tabIndex={0}
                          onClick={() => {
                            if (!isSelected) {
                              setCurrentSelectionSource({
                                type: 'bluebeamDms',
                                providerType,
                                drives,
                                drive: drives.first(),
                              });
                            }
                          }}
                        >
                          <Icon
                            className={`mr2 wrem-1p5 ${
                              isSelected ? 'fill-primary' : 'fill-gray-8'
                            }`}
                          />
                          {getAccountShortName(providerType) === 'Account'
                            ? translate(
                                'integrationsPage:accountDefaultShortName'
                              )
                            : getAccountShortName(providerType)}
                        </div>
                      );
                    })}
              </div>
            )
          )}
        </div>
        <div className="h100 px5 pt6 pb8 bl-3" style={{ width: '557px' }}>
          {!drawingsDisabled && currentSelectionSource.type === 'drawings' && (
            <div className="w100 h100">
              <>
                <div className="font-size-7 pt6 pb5 black-2 medium">
                  {translate('insertFilesModal:drawings.title')}
                </div>
                <FileBrowserBar
                  selectedCount={selectedCount}
                  searchInput={searchInput}
                  onSearchChange={handleSearchChange}
                  className="mb2"
                  countLabel={translate('insertFilesModal:selectedItems')}
                />
                <div className="oh" style={{ height: '485px' }}>
                  <DrawingsBrowser className="h100" search={searchInput} />
                </div>
              </>
            </div>
          )}
          {currentSelectionSource.type === 'studio' && (
            <div className="w100 h100">
              <div className="font-size-7 pt6 pb5 black-2 medium">
                {translate('insertFilesModal:studio.title')}
              </div>
              <FileBrowserBar
                selectedCount={selectedCount}
                searchInput={searchInput}
                onSearchChange={handleSearchChange}
                className="mb2"
                countLabel={translate('insertFilesModal:selectedItems')}
              />
              <BreadcrumbsOfAppState providerType="studio" />
              <div className="os pb2 mt1" style={{ height: '452px' }}>
                <StudioProjectsList searchInput={searchInput} />
              </div>
            </div>
          )}
          {currentSelectionSource.type === 'none' && (
            <div className="vh66 column justify-center items-center pt8">
              {!projectDrives ? (
                <LoadingAnimation
                  align="start"
                  removePadding
                  pageName="Loading"
                />
              ) : (
                <>
                  <div className="font-size-4 mt2 black-2 medium">
                    {translate(
                      'insertFilesModal:integrations.emptyState.title'
                    )}
                  </div>
                  <div className="font-size-4 mt2 black-2">
                    {translate('insertFilesModal:integrations.emptyState.text')}
                  </div>
                </>
              )}
            </div>
          )}
          {currentSelectionSource.type === 'bluebeamDms' &&
            (() => {
              const { providerType, drives, drive } = currentSelectionSource;
              const acct = getAccountName(providerType);
              const acctName =
                acct === 'Connection'
                  ? translate('integrationsPage:default.title')
                  : acct;
              return (
                <div className="w100 h100">
                  <div className="font-size-7 pt6 pb5 black-2 medium">
                    {acctName}
                  </div>
                  <Label label={translate('formLabels:connectedAccount')} />
                  <SingleSelect
                    id="insert-files-modal-connected-account-select"
                    options={drives
                      .map((d) => ({
                        label: d.connectionPrincipal,
                        value: d.driveId,
                      }))
                      .sortBy(({ label }) => label)
                      .toArray()}
                    value={{
                      label: drive.connectionPrincipal,
                      value: drive.driveId,
                    }}
                    onChange={({ value: selectedDriveId }): void =>
                      setCurrentSelectionSource({
                        ...currentSelectionSource,
                        drive:
                          drives.find((d) => d.driveId === selectedDriveId) ||
                          (drives.first() as ProjectDrive),
                      })
                    }
                  />
                  <div className="pt4 pb8">
                    <FileBrowserBar
                      selectedCount={selectedCount}
                      searchInput={searchInput}
                      onSearchChange={handleSearchChange}
                      className="mb2"
                      countLabel={translate('insertFilesModal:selectedItems')}
                    />
                    <div className="oh vh50">
                      <ProjectFileBrowser
                        providerType={providerType}
                        projectDriveId={drive.driveId}
                        initialSelectedItems={projectDriveSelectedItems.toArray()}
                        filter={searchInput}
                        foldersUnselectable
                        onlyImageFiles={onlyImagesSelectable}
                        clearSearchFilter={clearSearchFilter}
                      />
                    </div>
                  </div>
                </div>
              );
            })()}

          {currentSelectionSource.type === 'connectedAccount' &&
            (() => {
              const { providerType, drive } = currentSelectionSource;
              const acct = getAccountName(providerType);
              const acctName =
                acct === 'Connection'
                  ? translate('integrationsPage:default.title')
                  : acct;

              return (
                <div className="w100 h100">
                  <div className="font-size-7 pt6 pb5 black-2 medium">
                    {acctName} ({drive.label})
                  </div>
                  <div className="pb8">
                    <FileBrowserBar
                      selectedCount={selectedCount}
                      searchInput={searchInput}
                      onSearchChange={handleSearchChange}
                      className="mb2"
                      countLabel={translate('insertFilesModal:selectedItems')}
                    />
                    <div className="oh vh50">
                      <ConnectedAccountFileBrowser
                        providerType={providerType}
                        connectionId={drive.id}
                        filter={searchInput}
                        areFoldersSelectable={false}
                      />
                    </div>
                  </div>
                </div>
              );
            })()}
          <div className="row px5 py3 so-vw100 w100 bgd-white-2 justify-between modal__button-row modal__button-row--with-border">
            <button
              id="insert-files-modal-close"
              type="button"
              className="btn btn-link"
              onClick={handleCancelClick}
            >
              {translate('buttons:cancel')}
            </button>
            <button
              id="insert-files-modal-submit"
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={selectedCount === 0}
            >
              {translate('buttons:submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withAppStore<Props>(InsertFilesModalFC);
